<script setup>
/**
 * This component handles parsing and sending WhatsApp message templates.
 * It works as follows:
 * 1. Displays the template text with variable placeholders.
 * 2. Generates input fields for each variable in the template.
 * 3. Validates that all variables are filled before sending.
 * 4. Replaces placeholders with user-provided values.
 * 5. Emits events to send the processed message or reset the template.
 */
import { ref, computed, onMounted, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { DirectUpload } from 'activestorage';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useMapGetter } from 'dashboard/composables/store';
import {
  allKeysRequired,
  DEFAULT_LANGUAGE,
  DEFAULT_CATEGORY,
} from 'dashboard/helper/templateHelper';
import { WhatsAppTemplateDocument } from 'dashboard/services/WhatsAppTemplateDocument';

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
    validator: value => {
      if (!value || typeof value !== 'object') return false;
      if (!value.components || !Array.isArray(value.components)) return false;
      return true;
    },
  },
  enableMediaUpload: {
    type: Boolean,
    default: false,
  },
  showPreview: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['sendMessage', 'resetTemplate', 'back']);

const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');

const processedParams = ref({});
const headerMediaInput = ref(null);
const headerMediaSignedId = ref('');
const headerMediaName = ref('');
const headerMediaPreviewUrl = ref('');
const headerMediaUploadError = ref('');
const isHeaderMediaUploading = ref(false);

const SUPPORTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const DEFAULT_IMAGE_URL =
  'https://chatwoot.cats-fcis.org/vite/fcis-whatsapp-default-campus.jpg';

const templateDocument = computed(() =>
  WhatsAppTemplateDocument.fromTemplate(props.template, processedParams.value)
);

const languageLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.LANGUAGE')}: ${props.template.language || DEFAULT_LANGUAGE}`;
});

const categoryLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.CATEGORY')}: ${props.template.category || DEFAULT_CATEGORY}`;
});

const headerComponent = computed(() => templateDocument.value.header);

const headerText = computed(() => {
  return templateDocument.value.headerText;
});

const footerText = computed(() => templateDocument.value.footerText);
const previewButtons = computed(() => templateDocument.value.buttons);
const hasMediaHeader = computed(() => templateDocument.value.hasMediaHeader);

const formatType = computed(() => {
  const format = headerComponent.value?.format;
  return format ? format.charAt(0) + format.slice(1).toLowerCase() : '';
});

const isDocumentTemplate = computed(() => {
  return templateDocument.value.isDocument;
});

const isImageTemplate = computed(() => {
  return templateDocument.value.isImage;
});

const canUploadHeaderMedia = computed(
  () => props.enableMediaUpload && isImageTemplate.value
);

const hasVariables = computed(() => templateDocument.value.hasVariables);
const renderedTemplate = computed(() => templateDocument.value.renderedBody);

const headerPreviewUrl = computed(
  () =>
    headerMediaPreviewUrl.value || processedParams.value.header?.media_url || ''
);

const previewTime = new Intl.DateTimeFormat([], {
  hour: '2-digit',
  minute: '2-digit',
}).format(new Date());

const isFormInvalid = computed(
  () =>
    !templateDocument.value.isComplete({
      mediaSignedId: headerMediaSignedId.value,
    })
);

const v$ = useVuelidate(
  {
    processedParams: {
      requiredIfKeysPresent: requiredIf(hasVariables),
      allKeysRequired,
    },
  },
  { processedParams }
);

const updateMediaUrl = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_url = value;
};

const updateMediaName = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_name = value;
};

const clearHeaderMedia = () => {
  if (headerMediaPreviewUrl.value) {
    URL.revokeObjectURL(headerMediaPreviewUrl.value);
  }
  headerMediaSignedId.value = '';
  headerMediaName.value = '';
  headerMediaPreviewUrl.value = '';
  headerMediaUploadError.value = '';
  isHeaderMediaUploading.value = false;
  if (headerMediaInput.value) {
    headerMediaInput.value.value = '';
  }
};

const initializeTemplateParameters = () => {
  clearHeaderMedia();
  processedParams.value = templateDocument.value.initialParameters({
    defaultImageUrl: DEFAULT_IMAGE_URL,
  });
};

const openHeaderMediaPicker = () => {
  headerMediaInput.value?.click();
};

const handleHeaderMediaUpload = event => {
  const file = event.target.files?.[0];
  if (!file) return;

  headerMediaUploadError.value = '';

  if (!SUPPORTED_IMAGE_TYPES.includes(file.type)) {
    headerMediaUploadError.value = t(
      'CAMPAIGN.WHATSAPP.CREATE.FORM.MEDIA.INVALID_TYPE'
    );
    event.target.value = '';
    return;
  }

  if (file.size > MAX_IMAGE_SIZE) {
    headerMediaUploadError.value = t(
      'CAMPAIGN.WHATSAPP.CREATE.FORM.MEDIA.TOO_LARGE'
    );
    event.target.value = '';
    return;
  }

  isHeaderMediaUploading.value = true;
  const upload = new DirectUpload(
    file,
    `/api/v1/accounts/${accountId.value}/campaign_direct_uploads`,
    {
      directUploadWillCreateBlobWithXHR: xhr => {
        xhr.setRequestHeader(
          'api_access_token',
          currentUser.value.access_token
        );
      },
    }
  );

  upload.create((error, blob) => {
    isHeaderMediaUploading.value = false;
    if (error) {
      headerMediaUploadError.value = t(
        'CAMPAIGN.WHATSAPP.CREATE.FORM.MEDIA.UPLOAD_FAILED'
      );
      event.target.value = '';
      return;
    }

    if (headerMediaPreviewUrl.value) {
      URL.revokeObjectURL(headerMediaPreviewUrl.value);
    }
    headerMediaSignedId.value = blob.signed_id;
    headerMediaName.value = file.name;
    headerMediaPreviewUrl.value = URL.createObjectURL(file);
    processedParams.value.header ??= {};
    processedParams.value.header.media_url = `${window.location.origin}/rails/active_storage/blobs/redirect/${blob.signed_id}/${encodeURIComponent(file.name)}`;
  });
};

const sendMessage = () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  emit('sendMessage', templateDocument.value.messagePayload());
};

const resetTemplate = () => {
  emit('resetTemplate');
};

const goBack = () => {
  emit('back');
};

onMounted(initializeTemplateParameters);

watch(
  () => props.template,
  () => {
    initializeTemplateParameters();
    v$.value.$reset();
  },
  { deep: true }
);

defineExpose({
  processedParams,
  headerMediaSignedId,
  isHeaderMediaUploading,
  hasVariables,
  hasMediaHeader,
  isDocumentTemplate,
  headerComponent,
  isFormInvalid,
  renderedTemplate,
  v$,
  updateMediaUrl,
  updateMediaName,
  sendMessage,
  resetTemplate,
  goBack,
});
</script>

<template>
  <div>
    <section
      v-if="showPreview"
      class="mb-4 overflow-hidden rounded-xl border border-n-weak bg-n-background"
    >
      <div class="px-4 py-3 border-b border-n-weak">
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ t('WHATSAPP_TEMPLATES.PARSER.PREVIEW_LABEL') }}
        </h3>
      </div>
      <div class="p-4 bg-[#efeae2] dark:bg-n-slate-4">
        <div
          class="mx-auto max-w-sm overflow-hidden rounded-xl bg-white text-slate-900 shadow-sm"
        >
          <img
            v-if="headerPreviewUrl && isImageTemplate"
            :src="headerPreviewUrl"
            :alt="headerMediaName || template.name"
            class="object-cover w-full max-h-56"
          />
          <div
            v-else-if="hasMediaHeader"
            class="grid h-32 place-items-center bg-slate-100 text-sm font-medium text-slate-500"
          >
            {{ formatType }}
          </div>
          <div class="px-3 pt-3 pb-2">
            <p v-if="headerText" class="mb-1 text-sm font-semibold">
              {{ headerText }}
            </p>
            <p class="text-sm leading-5 whitespace-pre-wrap">
              {{ renderedTemplate }}
            </p>
            <div class="flex items-end justify-between gap-3 mt-1">
              <p v-if="footerText" class="text-xs text-slate-500">
                {{ footerText }}
              </p>
              <time class="ml-auto text-[10px] text-slate-400">
                {{ previewTime }}
              </time>
            </div>
          </div>
          <div
            v-for="(button, index) in previewButtons"
            :key="`${button.type}-${index}`"
            class="flex items-center justify-center gap-2 min-h-11 px-3 border-t border-slate-200 text-sm font-medium text-emerald-700"
          >
            <Icon
              :icon="
                button.type === 'QUICK_REPLY'
                  ? 'i-woot-quick-reply'
                  : button.type === 'PHONE_NUMBER'
                    ? 'i-lucide-phone'
                    : 'i-lucide-external-link'
              "
              class="size-4"
            />
            <span>{{ button.text }}</span>
          </div>
        </div>
      </div>
    </section>

    <div
      v-if="!showPreview"
      class="flex flex-col gap-4 p-4 mb-4 rounded-lg bg-n-alpha-black2"
    >
      <div class="flex justify-between items-center">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ template.name }}
        </h3>
        <span class="text-xs text-n-slate-11">
          {{ languageLabel }}
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <div class="rounded-md">
          <div class="text-sm whitespace-pre-wrap text-n-slate-12">
            {{ renderedTemplate }}
          </div>
        </div>
      </div>

      <div class="text-xs text-n-slate-11">
        {{ categoryLabel }}
      </div>
    </div>

    <div v-if="hasVariables || hasMediaHeader">
      <div v-if="hasMediaHeader" class="mb-4">
        <p class="mb-2.5 text-sm font-semibold">
          {{
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_LABEL', {
              type: formatType,
            }) || `${formatType} Header`
          }}
        </p>
        <div v-if="canUploadHeaderMedia" class="flex flex-col gap-2.5 mb-2.5">
          <input
            ref="headerMediaInput"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            class="hidden"
            @change="handleHeaderMediaUpload"
          />
          <div class="flex items-center gap-3">
            <Button
              type="button"
              variant="faded"
              color="slate"
              :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.MEDIA.UPLOAD_BUTTON')"
              :is-loading="isHeaderMediaUploading"
              :disabled="isHeaderMediaUploading"
              @click="openHeaderMediaPicker"
            />
            <span v-if="headerMediaName" class="text-sm text-n-slate-11">
              {{ headerMediaName }}
            </span>
          </div>
          <img
            v-if="headerMediaPreviewUrl"
            :src="headerMediaPreviewUrl"
            :alt="headerMediaName"
            class="object-contain w-full max-h-48 rounded-md bg-n-alpha-black2"
          />
          <p class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.MEDIA.HELP') }}
          </p>
          <p v-if="headerMediaUploadError" class="text-xs text-n-ruby-9">
            {{ headerMediaUploadError }}
          </p>
        </div>
        <div v-else class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_url || ''"
            type="url"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_LABEL', {
                type: formatType,
              })
            "
            @update:model-value="updateMediaUrl"
          />
        </div>
        <div v-if="isDocumentTemplate" class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_name || ''"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.DOCUMENT_NAME_PLACEHOLDER')
            "
            @update:model-value="updateMediaName"
          />
        </div>
      </div>

      <!-- Body Variables Section -->
      <div v-if="processedParams.body">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams.body"
          :key="`body-${key}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.body[key]"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
          />
        </div>
      </div>

      <!-- Button Variables Section -->
      <div v-if="processedParams.buttons">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
        </p>
        <div
          v-for="(button, index) in processedParams.buttons"
          :key="`button-${index}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.buttons[index].parameter"
            type="text"
            class="flex-1"
            :placeholder="t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')"
          />
        </div>
      </div>
      <p
        v-if="v$.$dirty && v$.$invalid"
        class="p-2.5 text-center rounded-md bg-n-ruby-9/20 text-n-ruby-9"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>

    <slot
      name="actions"
      :send-message="sendMessage"
      :reset-template="resetTemplate"
      :go-back="goBack"
      :is-valid="!v$.$invalid"
      :disabled="isFormInvalid"
    />
  </div>
</template>
